import { Alert, Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";

// An open-ended answer rule describes the SET of right answers, which no list of stored values can.
// "Write down a number between 0.3 and 2/5" has infinitely many; "two multiples of 2001" has
// infinitely many pairs; "a point the same distance from D as from C" is a whole line. The stored
// correct answer stays — it is what the reveal falls back on and what the AI grader reads — but a
// rule, once set, decides the verdict on its own.
const KINDS = [
  { value: "", label: "No rule — match the stored answer" },
  { value: "range", label: "Any value in a range" },
  { value: "multiple_of", label: "Any multiple of a number" },
  { value: "factor_of", label: "Any factor of a number" },
  { value: "member_of", label: "Any value from a list" },
  { value: "coordinate", label: "Coordinate with a free axis" },
  { value: "product", label: "Values that multiply to a total" },
  { value: "sum", label: "Values that add to a total" },
  { value: "derived", label: "Depends on an earlier part's answer" },
  { value: "congruence", label: "Congruence class (n ≡ r mod m)" },
];

// The written form the answer must take. A range alone cannot express "write down any FRACTION between
// 1/3 and 1/2": 0.4 sits inside the range and is still the wrong form. Nothing ticked = every form is
// accepted, which is how every rule authored before this behaved.
const FORMS = [
  { value: "fraction", label: "Fraction", hint: "5/12, 1 1/2, \\frac{5}{12}" },
  { value: "decimal", label: "Decimal", hint: "0.4 (a whole number counts too)" },
  { value: "whole", label: "Whole number", hint: "7" },
  { value: "percent", label: "Percentage", hint: "40%" },
];

const HELP = {
  range: 'Accepts any number strictly between the two values (tick "include the endpoints" for ≤ / ≥). Fractions, mixed numbers and LaTeX are all read as their value, so 7/20 passes a 0.3–0.4 range — tick a required form below when only a fraction (or only a decimal) will do.',
  multiple_of: 'Accepts any multiple of the number. Optional "ends with" digit (e.g. multiple of 12 ending in 2 → 12, 72, 132…). "Write down two multiples of 2001" — each box is checked, and the same value twice does not count twice.',
  factor_of: 'Accepts any factor of the number. The reveal lists them, so 2001 shows "1, 3, 23, 29, 69, 87, 667, 2001".',
  member_of: "Accepts any one of the values you list. Use this when the set is short and not a range or a multiple.",
  coordinate: 'Leave an axis blank (or type *) to accept any value there — "any point on the line x = 1".',
  product: "The values in the boxes must multiply to this total — a rectangle with the same area as a 24 cm square accepts 32×18, 24×24, 16×36 and every other pair.",
  sum: "The values in the boxes must add to this total.",
  derived: "The right answer is worked out from the answer the student gave to an earlier part of this same grouped question — e.g. the perimeter of whatever rectangle they drew.",
  congruence: 'Accepts any integer n where n ≡ residue (mod modulus). Example: modulus 60, residue 12 → 12, 72, 132, 192… (multiples of 4 and 6 that end in 2).',
};

const AnswerRuleEditor = ({ rule, setRule, blankCount = 0 }) => {
  const value = rule && typeof rule === "object" ? rule : {};
  const kind = value.kind || "";

  const patch = (changes) => setRule({ ...value, ...changes });

  const setKind = (next) => {
    if (!next) return setRule(null);
    // Carrying a "min" over to a multiple_of rule stores a field that kind never reads and that the
    // author cannot see — start each kind from a clean object, keeping only the shared toggles.
    setRule({
      kind: next,
      distinct: value.distinct !== false,
      integers_only: !!value.integers_only,
      forms: Array.isArray(value.forms) ? value.forms : [],
      display: value.display || "",
    });
  };

  const forms = Array.isArray(value.forms) ? value.forms : [];
  // Kept in FORMS order, not tick order — the reveal reads the list out ("Any fraction or decimal …").
  const toggleForm = (form, on) =>
    patch({
      forms: FORMS.map((f) => f.value).filter((v) => (v === form ? on : forms.includes(v))),
    });

  const numField = (name, label, placeholder, width = 140) => (
    <TextField
      size="small"
      label={label}
      value={value[name] ?? ""}
      onChange={(e) => patch({ [name]: e.target.value })}
      placeholder={placeholder}
      sx={{ width }}
    />
  );

  return (
    <Box sx={{ mt: 2, p: 1.5, border: "1px dashed #bbb", borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", display: "block", mb: 0.5 }}>
        Open-ended answer rule (optional)
      </Typography>
      <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 1 }}>
        Use this when the question has too many right answers to list — "any number between…", "any two
        multiples of…", "any point on this line". Leave it on "No rule" for ordinary questions.
      </Typography>

      <FormControl size="small" sx={{ minWidth: 290 }}>
        <InputLabel>Answer rule</InputLabel>
        <Select label="Answer rule" value={kind} onChange={(e) => setKind(e.target.value)}>
          {KINDS.map((k) => (
            <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {!!kind && (
        <>
          <Alert severity="info" sx={{ py: 0, mt: 1, mb: 1 }}>
            <Typography variant="caption">
              {HELP[kind]} While a rule is set it decides the verdict on its own — the stored answer and
              the AI grader are not consulted.
            </Typography>
          </Alert>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {kind === "range" && (
              <>
                {numField("min", "From", "0.3")}
                {numField("max", "To", "0.4")}
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!value.inclusive}
                      onChange={(e) => patch({ inclusive: e.target.checked })}
                    />
                  }
                  label={<Typography variant="caption">Include the endpoints</Typography>}
                />
              </>
            )}

            {(kind === "multiple_of" || kind === "factor_of") && (
              <>
                {numField("of", kind === "multiple_of" ? "Multiple of" : "Factor of", "2001")}
                {kind === "multiple_of" && numField("ends_with", "Ends with (optional)", "2", 120)}
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={value.allow_trivial !== false}
                      onChange={(e) => patch({ allow_trivial: e.target.checked })}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      {kind === "multiple_of" ? "Allow the number itself" : "Allow 1 and the number itself"}
                    </Typography>
                  }
                />
              </>
            )}

            {kind === "congruence" && (
              <>
                {numField("modulus", "Modulus", "60")}
                {numField("residue", "Residue", "12")}
              </>
            )}

            {kind === "member_of" && (
              <TextField
                size="small"
                fullWidth
                label="Accepted values (comma separated)"
                value={(value.values || []).join(", ")}
                onChange={(e) =>
                  patch({ values: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) })
                }
                placeholder="1, 3, 23, 29, 69, 87, 667, 2001"
              />
            )}

            {kind === "coordinate" && (
              <>
                {numField("x", "x must be", "1 or *")}
                {numField("y", "y must be", "* for any")}
              </>
            )}

            {(kind === "product" || kind === "sum") && (
              <>
                {numField("value", kind === "product" ? "Product" : "Total", "576")}
                {blankCount < 2 && (
                  <Typography variant="caption" sx={{ color: "#b26a00" }}>
                    Needs at least two answer boxes.
                  </Typography>
                )}
              </>
            )}

            {kind === "derived" && (
              <>
                <TextField
                  size="small"
                  label="Earlier part (text to look for)"
                  value={value.source ?? ""}
                  onChange={(e) => patch({ source: e.target.value })}
                  placeholder="sketch of a rectangle"
                  sx={{ width: 280 }}
                />
                <TextField
                  size="small"
                  label="Worked out as"
                  value={value.expression ?? ""}
                  onChange={(e) => patch({ expression: e.target.value })}
                  placeholder="2*(a+b)"
                  helperText="a, b, c… are that part's answer boxes, left to right"
                  sx={{ width: 220 }}
                />
              </>
            )}
          </Box>

          {kind !== "coordinate" && kind !== "derived" && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "#555", fontWeight: 700, display: "block" }}>
                The answer must be written as
              </Typography>
              <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 0.25 }}>
                Tick one or several — "write down any fraction between ⅓ and ½" accepts 5/12 but not 0.4.
                Leave them all clear to accept any form.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                {FORMS.map((f) => (
                  <FormControlLabel
                    key={f.value}
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={forms.includes(f.value)}
                        onChange={(e) => toggleForm(f.value, e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        {f.label} <Typography component="span" variant="caption" sx={{ color: "#999" }}>({f.hint})</Typography>
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {kind !== "coordinate" && kind !== "derived" && (
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={!!value.integers_only}
                    onChange={(e) => patch({ integers_only: e.target.checked })}
                  />
                }
                label={<Typography variant="caption">Whole numbers only</Typography>}
              />
            )}
            {blankCount > 1 && kind !== "coordinate" && kind !== "derived" && kind !== "product" && kind !== "sum" && (
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={value.distinct !== false}
                    onChange={(e) => patch({ distinct: e.target.checked })}
                  />
                }
                label={<Typography variant="caption">The boxes must hold different values</Typography>}
              />
            )}
          </Box>

          <TextField
            size="small"
            fullWidth
            label="What to show as the correct answer (optional)"
            value={value.display ?? ""}
            onChange={(e) => patch({ display: e.target.value })}
            placeholder="Any decimal or fraction between 0.3 and 0.4"
            helperText="Shown only after the student answers. Leave blank to let the rule describe itself."
            sx={{ mt: 1 }}
          />
        </>
      )}
    </Box>
  );
};

export default AnswerRuleEditor;
